import { ApplicationException, CardTypeConstants } from '@uzurv/core';
import { CardType, OrganizationPaymentAccount } from '../models';
import { CreditCard } from 'angular-cc-library';

export class PaymentUtil {
  static getCardTypeCodeFromCreditCard(creditCardNumber: string): string {
    const creditCard = CreditCard.cardFromNumber(creditCardNumber);

    if (!creditCard || !creditCard.type) {
      return null;
    }

    switch (creditCard.type) {
      case 'amex': {
        return CardTypeConstants.CARD_TYPE_CODE_AMEX;
      }
      case 'dinersclub': {
        return CardTypeConstants.CARD_TYPE_CODE_DINERS_CLUB;
      }
      case 'discover': {
        return CardTypeConstants.CARD_TYPE_CODE_DISCOVER;
      }
      case 'visa': {
        return CardTypeConstants.CARD_TYPE_CODE_VISA;
      }
      case 'mastercard': {
        return CardTypeConstants.CARD_TYPE_CODE_MASTER_CARD;
      }
      case 'jcb': {
        return CardTypeConstants.CARD_TYPE_CODE_JCB;
      }
      default: {
        return null;
      }
    }
  }

  static getCreditCardLogoSVG(cardType: CardType): string {
    const cardTypeCode = cardType?.code;

    switch (cardTypeCode) {
      case CardTypeConstants.CARD_TYPE_CODE_AMEX:
        return 'uz-credit-card-issuer-amex';

      case CardTypeConstants.CARD_TYPE_CODE_DISCOVER:
        return 'uz-credit-card-issuer-discover';

      case CardTypeConstants.CARD_TYPE_CODE_MASTER_CARD:
        return 'uz-credit-card-issuer-mastercard';

      case CardTypeConstants.CARD_TYPE_CODE_VISA:
        return 'uz-credit-card-issuer-visa';

      case CardTypeConstants.CARD_TYPE_CODE_DINERS_CLUB:
        return 'uz-credit-card-issuer-dinersclub';

      case CardTypeConstants.CARD_TYPE_CODE_JCB:
        return 'uz-credit-card-issuer-jcb';

      default:
        return 'uz-credit-card-issuer-default';
    }
  }

  static formatCreditCardExpirationDate(expirationDate: string): string {
    return `${expirationDate.split('/')[1]}-${expirationDate.split('/')[0]}`.trim();
  }

  static validateCreditCardExpirationDate(expirationDate: string): void {
    const expirationDateRegex = /^(0[1-9]|1[0-2])\s*\/\s*\d{4}$/;
    if (!expirationDateRegex.test(expirationDate?.trim() ?? '')) {
      const error = new ApplicationException('The card expiration date is required to be in MM/YYYY format');
      error.name = 'Card expiration date invalid';
      throw error;
    }
  }

  static validateCreditCard(creditCardNumber: string, creditCardExpirationDate: string, cardTypes: Array<CardType>): void {
    const cardTypeCode = PaymentUtil.getCardTypeCodeFromCreditCard(creditCardNumber);
    const cardType: CardType = cardTypes?.find(ct => ct.code === cardTypeCode);

    PaymentUtil.validateCreditCardExpirationDate(creditCardExpirationDate);

    if (!CreditCard.cardFromNumber(creditCardNumber)) {
      const error = new ApplicationException('Invalid credit card number');
      error.name = 'Invalid Credit Card';
      throw error;
    }

    if (!cardType) {
      const error = new ApplicationException(`Couldn't find card type ${cardTypeCode}`);
      error.name = 'No Card Type';
      throw error;
    }
  }

  static isDefaultOrganizationPaymentAccountExpired(organizationPaymentAccounts: Array<OrganizationPaymentAccount>): boolean {
    return organizationPaymentAccounts.some(
      organizationPaymentAccount =>
        organizationPaymentAccount.default &&
        organizationPaymentAccount.paymentAccount.cardExpiration &&
        new Date(organizationPaymentAccount.paymentAccount.cardExpiration) < new Date()
    );
  }
}
